'use client';

import { c as _c } from "react/compiler-runtime";
import type { ChainFarmData, FarmRewardPrices, NativeTokenPrices } from '@/constants/project-specific/farms/types';
import type { RequiredFarmChainType } from '@/constants/project-specific';
import { useOptimizedFarmData } from './useOptimizedFarmData';
const PONDER_API_URL = process.env.NEXT_PUBLIC_PONDER_API_URL || 'http://localhost:42069';
interface PonderFarmPool {
  id: string;
  chainId: number;
  masterChefAddress: string;
  poolId: number;
  lpToken: string;
  allocPoint: string;
  totalDeposits: string;
  isActive: boolean;
  // ... other fields
}
interface PonderApiResponse<T> {
  success: boolean;
  data: T;
  error?: string;
}
export function usePonderFarmData(prices, nativePrices, chainIds) {
  const $ = _c(8);
  const t0 = chainIds as RequiredFarmChainType[];
  let t1;
  if ($[0] !== nativePrices || $[1] !== prices || $[2] !== t0) {
    t1 = {
      prices,
      nativePrices,
      chainIds: t0,
      enabled: true
    };
    $[0] = nativePrices;
    $[1] = prices;
    $[2] = t0;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const {
    data,
    isLoading: loading,
    error,
    totalCalls
  } = useOptimizedFarmData(t1);
  console.log(`📊 Optimized farm data loading: ${totalCalls} total contract calls batched via multicall`);
  const t2 = error?.message || null;
  let t3;
  if ($[4] !== data || $[5] !== loading || $[6] !== t2) {
    t3 = {
      data,
      loading,
      error: t2,
      usedFallback: true
    };
    $[4] = data;
    $[5] = loading;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  return t3;
}
async function fetchFromPonderAPI(chainIds?: number[]): Promise<ChainFarmData | null> {
  const timeout = 5000; // 5 second timeout for Ponder API

  try {
    // Health check first
    const healthResponse = await fetch(`${PONDER_API_URL}/health`, {
      signal: AbortSignal.timeout(timeout),
      headers: {
        'Accept': 'application/json'
      }
    });
    if (!healthResponse.ok) {
      throw new Error(`Ponder API health check failed: ${healthResponse.status}`);
    }

    // If specific chain IDs are provided, fetch for each chain
    const targetChainIds = chainIds || [109, 2000]; // Default to Shibarium and Dogechain
    const result: ChainFarmData = {
      109: [],
      2000: []
    };
    for (const chainId of targetChainIds) {
      try {
        const response = await fetch(`${PONDER_API_URL}/farms?chainId=${chainId}`, {
          signal: AbortSignal.timeout(timeout),
          headers: {
            'Accept': 'application/json'
          }
        });
        if (!response.ok) {
          console.warn(`Failed to fetch farms for chain ${chainId}: ${response.status}`);
          result[chainId as keyof ChainFarmData] = [];
          continue;
        }
        const apiResponse: PonderApiResponse<PonderFarmPool[]> = await response.json();
        if (!apiResponse.success) {
          console.warn(`Ponder API returned error for chain ${chainId}:`, apiResponse.error);
          result[chainId as keyof ChainFarmData] = [];
          continue;
        }

        // Transform Ponder data to match the existing farm data structure
        const transformedPools = apiResponse.data.map(transformPonderPoolToFarmConfig);
        result[chainId as keyof ChainFarmData] = transformedPools;
        console.log(`📊 Fetched ${transformedPools.length} pools for chain ${chainId} from Ponder`);
      } catch (chainError) {
        console.warn(`Error fetching chain ${chainId} from Ponder:`, chainError);
        result[chainId as keyof ChainFarmData] = [];
      }
    }

    // Check if we got any data
    const totalPools = Object.values(result).reduce((sum, pools) => sum + pools.length, 0);
    if (totalPools === 0) {
      throw new Error('No farm data available from Ponder API');
    }
    return result;
  } catch (error) {
    console.error('Ponder API fetch error:', error);
    throw error;
  }
}
function transformPonderPoolToFarmConfig(ponderPool: PonderFarmPool): any {
  // Transform Ponder pool data to match existing FarmConfig structure
  // This is a simplified transformation - you may need to adjust based on your exact requirements
  return {
    id: ponderPool.poolId,
    lpAddress: ponderPool.lpToken,
    allocPoint: Number(ponderPool.allocPoint),
    totalStaked: ponderPool.totalDeposits,
    isActive: ponderPool.isActive,
    chainId: ponderPool.chainId,
    masterChefAddress: ponderPool.masterChefAddress
    // Add other fields as needed to match FarmConfig
  };
}